import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { getRitiri, getProduttori, getMezzi, deleteRitiro } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { formatCurrency, formatDateIt, formatNumber } from "../lib/constants";
import { Calendar, Truck, Droplets, Wallet, Trash2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { ConfirmDialog } from "../components/ConfirmDialog";

export default function Ritiri() {
  const { isAdmin } = useAuth();
  const [ritiri, setRitiri] = useState([]);
  const [produttoreId, setProduttoreId] = useState("");
  const [mezzoId, setMezzoId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [produttori, setProduttori] = useState([]);
  const [mezzi, setMezzi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (produttoreId && produttoreId !== "__all__") params.produttoreId = produttoreId;
    const r = await getRitiri(params);
    setRitiri(r);
    setLoading(false);
  };

  useEffect(() => {
    Promise.all([getProduttori(), getMezzi()]).then(([p, m]) => {
      setProduttori(p);
      setMezzi(m);
    });
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [produttoreId]);

  const produttoriById = useMemo(() => Object.fromEntries(produttori.map((p) => [p.id, p])), [produttori]);
  const mezziById = useMemo(() => Object.fromEntries(mezzi.map((m) => [m.id, m])), [mezzi]);

  const filtered = useMemo(() => {
    return ritiri.filter((r) => {
      if (mezzoId && mezzoId !== "__all__" && r.mezzoId !== mezzoId) return false;
      if (from) {
        const d = new Date(r.data); const f = new Date(from);
        if (d < f) return false;
      }
      if (to) {
        const d = new Date(r.data); const t = new Date(to); t.setHours(23, 59, 59);
        if (d > t) return false;
      }
      return true;
    });
  }, [ritiri, mezzoId, from, to]);

  const stats = useMemo(() => {
    const kg = filtered.reduce((a, r) => a + (r.quantitaKg || 0), 0);
    const importo = filtered.reduce((a, r) => a + (r.importo || 0), 0);
    return { count: filtered.length, kg, importo };
  }, [filtered]);

  return (
    <div className="space-y-4" data-testid="ritiri-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Ritiri</h1>
        <p className="text-slate-500 text-sm mt-1">Storico completo</p>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Card className="p-3 bg-white border border-slate-200">
          <div className="text-[10px] uppercase font-semibold text-slate-500">Totale ritiri</div>
          <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{formatNumber(stats.count)}</div>
        </Card>
        <Card className="p-3 bg-white border border-slate-200">
          <div className="text-[10px] uppercase font-semibold text-slate-500">Kg</div>
          <div className="text-2xl font-bold text-[#F57C00]" style={{ fontFamily: "Outfit, sans-serif" }}>{formatNumber(stats.kg)}</div>
        </Card>
        <Card className="p-3 bg-white border border-slate-200">
          <div className="text-[10px] uppercase font-semibold text-slate-500">Importo</div>
          <div className="text-2xl font-bold text-[#1B5E20]" style={{ fontFamily: "Outfit, sans-serif" }}>{formatCurrency(stats.importo)}</div>
        </Card>
      </div>

      <Card className="p-3 bg-white border border-slate-200 space-y-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Select value={produttoreId} onValueChange={setProduttoreId}>
            <SelectTrigger data-testid="filter-prod"><SelectValue placeholder="Tutti i produttori" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutti</SelectItem>
              {produttori.map((p) => <SelectItem key={p.id} value={p.id}>{p.ragioneSociale}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={mezzoId} onValueChange={setMezzoId}>
            <SelectTrigger data-testid="filter-mezzo"><SelectValue placeholder="Tutti i mezzi" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutti</SelectItem>
              {mezzi.map((m) => <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="filter-from" />
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="filter-to" />
        </div>
      </Card>

      {loading ? (
        <div className="p-4 text-slate-500">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center text-slate-500" data-testid="ritiri-empty">
          <Droplets size={36} className="mx-auto text-slate-300 mb-2" />
          Nessun ritiro trovato
        </Card>
      ) : (
        <ul className="space-y-2" data-testid="ritiri-list">
          {filtered.map((r) => {
            const p = produttoriById[r.produttoreId];
            const m = r.mezzoId ? mezziById[r.mezzoId] : null;
            return (
              <li key={r.id}>
                <Card className="p-4 bg-white border border-slate-200" data-testid={`ritiro-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{p?.ragioneSociale || "Produttore eliminato"}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-2 mt-1">
                        <Calendar size={11} /> {formatDateIt(r.data)}
                        {m && <><span className="h-2 w-2 rounded-full" style={{ backgroundColor: m.colore }} /> {m.nome}</>}
                      </div>
                      {r.operatore && <div className="text-[10px] text-slate-400 mt-0.5">Op: {r.operatore}</div>}
                      {r.note && <div className="text-xs text-slate-500 mt-1 italic">{r.note}</div>}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm text-slate-500">{formatNumber(r.quantitaKg)} kg</div>
                      <div className="font-bold text-[#1B5E20]" style={{ fontFamily: "Outfit, sans-serif" }}>{formatCurrency(r.importo)}</div>
                      {isAdmin && (
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => setDeleteId(r.id)}
                          className="text-[#C62828] hover:bg-[#C62828]/10 mt-1 h-7 px-2"
                          data-testid={`delete-ritiro-${r.id}`}
                          aria-label="Elimina ritiro"
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        open={!!deleteId}
        onCancel={() => setDeleteId(null)}
        onConfirm={async () => {
          try {
            await deleteRitiro(deleteId);
            toast.success("Ritiro eliminato");
            setDeleteId(null);
            await load();
          } catch (ex) {
            toast.error(ex.response?.data?.detail || "Errore");
          }
        }}
        title="Eliminare il ritiro?"
        description="L'azione non può essere annullata. I totali aziendali verranno ricalcolati."
        confirmLabel="Elimina"
        variant="destructive"
        testId="delete-ritiro-dialog"
      />
    </div>
  );
}
